import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/i18n/LanguageContext';
import { courseContentService } from '@/modules/lcms/content-courses/services/courseContentService';
import { ContentCourse, Module, Section, Activity, Question } from '@/modules/lcms/content-courses/schemas/schemas';
import { Skeleton } from '@/components/ui/skeleton';
import { ThemeSelectionDialog } from './ThemeSelectionDialog';
import { Theme } from './ThemeSelectionDialog';
import { ThemeSection } from './ThemeSection';
import { FullscreenCourseEditDialog } from './FullscreenCourseEditDialog';

interface Props {
  courseId: string;
}

export function ContentCourseDetail({ courseId }: Props) {
  const [course, setCourse] = useState<ContentCourse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [isThemeDialogOpen, setIsThemeDialogOpen] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState<Theme | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const { t } = useLanguage();

  // Helper function to translate activity types to Vietnamese
  const getActivityTypeLabel = (type: string) => {
    switch (type) {
      case 'lesson': return 'Bài học';
      case 'exercise': return 'Bài tập';
      case 'session': return 'Buổi học';
      case 'video': return 'Video';
      case 'practical': return 'Thực hành';
      case 'assessment': return 'Đánh giá';
      default: return type;
    }
  };

  // Helper function to translate delivery methods to Vietnamese
  const getDeliveryMethodLabel = (method: string) => {
    switch (method) {
      case 'combined': return 'Học tập tổng hợp';
      case 'e-learning': return 'Học trực tuyến';
      case 'live-online': return 'Học trực tiếp online';
      case 'in-person': return 'Học trực tiếp';
      case 'offline': return 'Học tại lớp';
      default: return method;
    }
  };

  // Helper function to translate module types to Vietnamese
  const getModuleTypeLabel = (type: string) => {
    switch (type) {
      case 'combined': return 'Tổng hợp';
      case 'e-learning': return 'Trực tuyến';
      case 'live-online': return 'Trực tiếp online';
      case 'in-person': return 'Trực tiếp';
      case 'offline': return 'Tại lớp';
      default: return type;
    }
  };

  // Helper function to get delivery method styling
  const getDeliveryMethodStyle = (method: string) => {
    switch (method) {
      case 'combined': 
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'e-learning': 
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'live-online': 
        return 'bg-green-100 text-green-800 border-green-200';
      case 'in-person': 
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'offline': 
        return 'bg-orange-100 text-orange-800 border-orange-200';
      default: 
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Helper function to get delivery method icon
  const getDeliveryMethodIcon = (method: string) => {
    switch (method) {
      case 'combined':
        return (
          <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
        );
      case 'e-learning':
        return (
          <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        );
      case 'live-online':
        return (
          <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        );
      case 'in-person':
        return (
          <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        );
      case 'offline':
        return (
          <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
          </svg>
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    const fetchCourse = async () => {
      setLoading(true);
      try {
        const data = await courseContentService.getCourse(courseId);
        setCourse(data);
        
        // Set default expanded state for all modules and sections
        if (data.modules && data.modules.length > 0) {
          const moduleIds = new Set(data.modules.map(m => m.id));
          setExpandedModules(moduleIds);
          
          // Keep sections collapsed by default - don't add them to expandedSections
          // const sectionIds = new Set<string>();
          // data.modules.forEach(module => {
          //   if (module.sections) {
          //     module.sections.forEach(section => {
          //       sectionIds.add(section.id);
          //     });
          //   }
          // });
          // setExpandedSections(sectionIds);
        }
        
        // Set default selected item if course has modules
        if (data.modules && data.modules.length > 0) {
          const firstModule = data.modules[0];
          
          // Check sections structure
          if (firstModule.sections && firstModule.sections.length > 0) {
            const firstSection = firstModule.sections[0];
            if (firstSection.activities && firstSection.activities.length > 0) {
              setSelectedItemId(`${firstModule.id}-activity-${firstSection.activities[0].activity_id}`);
            }
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load course');
      } finally {
        setLoading(false);
      }
    };

    fetchCourse();
  }, [courseId]);

  // Function to get selected item
  const getSelectedItem = () => {
    if (!course || !selectedItemId) return null;
    
    for (const module of course.modules || []) {
      if (module.sections) {
        for (const section of module.sections) {
          for (const activity of section.activities || []) {
            const activityId = `${module.id}-activity-${activity.activity_id}`;
            if (activityId === selectedItemId) {
              return activity;
            }
          }
        }
      }
    }
    return null;
  };

  const selectedItem = getSelectedItem();

  const handleThemeSelect = (theme: Theme) => {
    setSelectedTheme(theme);
    setIsThemeDialogOpen(false);
    // Here you would typically save the theme selection to your backend
    // courseService.updateCourseTheme(courseId, theme.id);
  };

  // Functions to handle direct offset changes






  // Toggle functions for expand/collapse
  const toggleModule = (moduleId: string) => {
    setExpandedModules(prev => {
      const newSet = new Set(prev);
      if (newSet.has(moduleId)) {
        newSet.delete(moduleId);
      } else {
        newSet.add(moduleId);
      }
      return newSet;
    });
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  const collapseAll = () => {
    setExpandedModules(new Set());
    setExpandedSections(new Set());
  };

  const expandAll = () => {
    if (course?.modules) {
      const moduleIds = new Set(course.modules.map(m => m.id));
      setExpandedModules(moduleIds);
      
      const sectionIds = new Set<string>();
      course.modules.forEach(module => {
        if (module.sections) {
          module.sections.forEach(section => {
            sectionIds.add(section.id);
          });
        }
      });
      setExpandedSections(sectionIds);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <Skeleton className="h-8 w-64" />
            <Skeleton className="mt-2 h-4 w-96" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="h-10 w-24" />
            <Skeleton className="h-10 w-24" />
            <Skeleton className="h-10 w-32" />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <div className="md:col-span-2">
            <Card>
              <CardContent className="p-6">
                <Skeleton className="h-96 w-full" />
              </CardContent>
            </Card>
          </div>
          <div>
            <Card>
              <CardContent className="p-6">
                <Skeleton className="h-64 w-full" />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600">{error || 'Course not found'}</h2>
          <p className="mt-2 text-gray-600">Please try again later</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with title, description and primary action buttons */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{course.title || course.name}</h1>
        </div>
        <div className="flex items-center gap-2">
          <Link to={`/lcms/course-content/${courseId}/preview`} target="_blank" rel="noopener noreferrer">
            <Button variant="outline">{t('course-content.detail.preview')}</Button>
          </Link>
          <Button variant="outline" onClick={() => setIsEditDialogOpen(true)}>{t('course-content.detail.edit')}</Button>
          
          {course.status === 'draft' && (
            <Button variant="primary">{t('course-content.detail.sendForApproval')}</Button>
          )}
          
          {course.status === 'pending_approval' && (
            <Button variant="primary">{t('course-content.detail.approve')}</Button>
          )}
          
          {course.status === 'approved' && (
            <Button variant="primary">{t('course-content.detail.publish')}</Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-1">
        <div className="md:col-span-2">
          <Card>
            <CardContent className="p-6 pt-6">
              {/* Course structure header with tools */}
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-xl font-semibold">{t('course-content.detail.contentCourse')}</h2>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm">Thêm chương</Button>
                  <Button variant="outline" size="sm">Thêm bài học</Button>
                  <Button variant="outline" size="sm">Thêm bài tập</Button>
                </div>
              </div>

              {/* Course structure toolbar */}
              <div className="mb-4 flex items-center gap-2 rounded-md bg-gray-50 p-2 dark:bg-gray-800">
                <Button variant="ghost" size="sm">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1">
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                  </svg>
                  Tạo mới
                </Button>
                <Button variant="ghost" size="sm">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1">
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"></path>
                    <line x1="10" y1="11" x2="10" y2="17"></line>
                    <line x1="14" y1="11" x2="14" y2="17"></line>
                  </svg>
                  Xóa
                </Button>
                <Button variant="ghost" size="sm">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1">
                    <line x1="12" y1="19" x2="12" y2="5"></line>
                    <polyline points="5 12 12 5 19 12"></polyline>
                  </svg>
                  {t('course-content.detail.moveUp')}
                </Button>
                <Button variant="ghost" size="sm">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1">
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <polyline points="19 12 12 19 5 12"></polyline>
                  </svg>
                  {t('course-content.detail.moveDown')}
                </Button>
                <Button variant="ghost" size="sm" onClick={collapseAll}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="8" y1="12" x2="16" y2="12"></line>
                  </svg>
                  {t('course-content.detail.collapseAll')}
                </Button>
                <Button variant="ghost" size="sm" onClick={expandAll}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="8" y1="12" x2="16" y2="12"></line>
                    <line x1="12" y1="8" x2="12" y2="16"></line>
                  </svg>
                  {t('course-content.detail.expandAll')}
                </Button>
              </div>

              {/* Course structure tree */}
              <div className="space-y-4">
                {(course.modules || []).length === 0 ? (
                  <div className="text-center py-8">
                    <div className="text-gray-500 dark:text-gray-400">
                      <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mx-auto mb-4">
                        <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                        <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
                      </svg>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                        {t('course-content.detail.noModules') || 'No modules found'}
                      </h3>
                      <p className="text-sm">
                        {t('course-content.detail.noModulesDescription') || 'This course doesn\'t have any modules yet. Click "Add Topic" to get started.'}
                      </p>
                    </div>
                  </div>
                ) : (
                  (course.modules || []).map((module, moduleIndex) => (
                    <div key={module.id} className="rounded-md border border-gray-200 dark:border-gray-800">
                      <div 
                        className="flex items-center justify-between bg-gray-50 px-4 py-3 dark:bg-gray-800"
                      >
                        {/* Module header with drag handle */}
                        <div className="flex items-center">
                          <span className="mr-2 cursor-move text-gray-400" onClick={(e) => e.stopPropagation()}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="9" cy="12" r="1"></circle>
                              <circle cx="9" cy="5" r="1"></circle>
                              <circle cx="9" cy="19" r="1"></circle>
                              <circle cx="15" cy="12" r="1"></circle>
                              <circle cx="15" cy="5" r="1"></circle>
                              <circle cx="15" cy="19" r="1"></circle>
                            </svg>
                          </span>
                          <span className="mr-2 text-gray-500 cursor-pointer hover:text-gray-700" onClick={() => toggleModule(module.id)}>
                            {expandedModules.has(module.id) ? (
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M6 9l6 6 6-6"></path>
                              </svg>
                            ) : (
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M9 18l6-6-6-6"></path>
                              </svg>
                            )}
                          </span>
                          <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
                            <div className="font-medium mr-3">{module.name}</div>
                            <div className={`flex items-center text-xs font-medium px-2 py-1 rounded-md border ${getDeliveryMethodStyle(module.type)}`}>
                              {getDeliveryMethodIcon(module.type)}
                              {getModuleTypeLabel(module.type)}
                            </div>
                          </div>
                        </div>

                      </div>
                      {expandedModules.has(module.id) && (
                        <div className="divide-y divide-gray-200 dark:divide-gray-800">
                          {/* Render sections structure */}
                          {module.sections && module.sections.map((section, sectionIndex) => (
                            <div key={section.id} className="p-4">
                              <div className="flex items-center justify-between mb-2 rounded-md p-2 -m-2">
                                <div className="flex items-center">
                                  <span className="mr-2 text-gray-500 cursor-pointer hover:text-gray-700" onClick={() => toggleSection(section.id)}>
                                    {expandedSections.has(section.id) ? (
                                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M6 9l6 6 6-6"></path>
                                      </svg>
                                    ) : (
                                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M9 18l6-6-6-6"></path>
                                      </svg>
                                    )}
                                  </span>
                                  <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
                                    <h4 className="font-medium text-gray-900 dark:text-gray-100 mr-3">{section.name}</h4>
                                    <div className={`flex items-center text-xs font-medium px-3 py-1 rounded-full border ${getDeliveryMethodStyle(section.delivery_method)}`}>
                                      {getDeliveryMethodIcon(section.delivery_method)}
                                      {getDeliveryMethodLabel(section.delivery_method)}
                                    </div>
                                  </div>
                                </div>

                              </div>
                              {expandedSections.has(section.id) && (
                                <div className="space-y-2 ml-4">
                                  {section.activities.map((activity, activityIndex) => {
                                    const activityId = `${module.id}-activity-${activity.activity_id}`;
                                    return (
                                      <div 
                                        key={activityId} 
                                        className={`flex items-center justify-between px-4 py-3 rounded-md cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 ${selectedItemId === activityId ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                                        onClick={() => setSelectedItemId(activityId)}
                                      >
                                        <div className="flex items-center">
                                          <span className="ml-4 mr-2 cursor-move text-gray-400">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                              <circle cx="9" cy="12" r="1"></circle>
                                              <circle cx="9" cy="5" r="1"></circle>
                                              <circle cx="9" cy="19" r="1"></circle>
                                              <circle cx="15" cy="12" r="1"></circle>
                                              <circle cx="15" cy="5" r="1"></circle>
                                              <circle cx="15" cy="19" r="1"></circle>
                                            </svg>
                                          </span>
                                          <span className="mr-2 text-gray-500">
                                            {activity.type === 'session' ? (
                                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                                  <line x1="16" y1="2" x2="16" y2="6"></line>
                                                  <line x1="8" y1="2" x2="8" y2="6"></line>
                                                  <line x1="3" y1="10" x2="21" y2="10"></line>
                                              </svg>
                                            ) : activity.type === 'exercise' ? (
                                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <circle cx="12" cy="12" r="10"></circle>
                                                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                                                <line x1="12" y1="17" x2="12.01" y2="17"></line>
                                              </svg>
                                            ) : (
                                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                                                <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
                                              </svg>
                                            )}
                                          </span>
                                          <div className="flex-1">
                                            <div className="font-medium">{activity.title}</div>
                                            <div className="text-sm text-gray-500 dark:text-gray-400">
                                              {getActivityTypeLabel(activity.type)}
                                              {activity.type === 'exercise' && ` (${(activity.questions || []).length} câu hỏi)`}
                                              {activity.type === 'session' && activity.default_platform && ` - ${activity.default_platform}`}
                                              {activity.type === 'session' && activity.default_location && ` - ${activity.default_location}`}
                                            </div>
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <Link 
                                            to={`/lcms/course-content/${courseId}/composer?itemId=${activityId}&type=${activity.type}`} 
                                          >
                                            <Button variant="ghost" size="sm">{t('course-content.detail.edit')}</Button>
                                          </Link>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="hidden">
          {/* Course details card */}
          <Card>
            <CardContent className="p-4 pt-4">
              <h2 className="mb-4 text-lg font-semibold">{t('course-content.detail.courseDetails')}</h2>
              <dl className="space-y-2">
                <div className="flex justify-between">
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('course-content.detail.status')}</dt>
                  <dd>
                    {course.status || 'draft'}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('course-content.detail.createdBy')}</dt>
                  <dd className="text-sm">{course.createdBy || course.author || '-'}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('course-content.detail.createdAt')}</dt>
                  <dd className="text-sm">{course.createdAt || '-'}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('course-content.detail.lastUpdated')}</dt>
                  <dd className="text-sm">{course.updatedAt || '-'}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Duration</dt>
                  <dd className="text-sm">{course.duration || '-'} hours</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Level</dt>
                  <dd className="text-sm">{course.level || '-'}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Course Type</dt>
                  <dd className="text-sm">{course.course_type || '-'}</dd>
                </div>
              </dl>
            </CardContent>
          </Card>

          {/* Theme Selection and Preview Card */}
          <div className="mt-6 hidden">
            <ThemeSection 
              selectedTheme={selectedTheme}
              onThemeChange={() => setIsThemeDialogOpen(true)}
            />
          </div>

          {/* Action buttons card */}
          <div className="mt-6 hidden">
            <Card>
              <CardContent className="p-6">
                <h2 className="mb-4 text-lg font-semibold">{t('course-content.detail.actions')}</h2>
                <div className="grid grid-cols-2 gap-2">
                  <Link to={`/lcms/course-content/${courseId}/composer`}>
                    <Button variant="outline" size="sm">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                      {t('course-content.detail.edit')}
                    </Button>
                  </Link>
                  <Button variant="outline" size="sm">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                    {t('course-content.detail.preview')}
                  </Button>
                  <Button variant="outline" size="sm">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
                      <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
                    </svg>
                    {t('course-content.detail.copy')}
                  </Button>
                  <Button variant="outline" size="sm">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="7 10 12 15 17 10"></polyline>
                      <line x1="12" y1="15" x2="12" y2="3"></line>
                    </svg>
                    {t('course-content.detail.export')}
                  </Button>
                </div>
                
                <div className="mt-4 grid grid-cols-2 gap-2">
                  {course.status === 'draft' && (
                    <Button variant="primary" size="sm">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                        <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path>
                        <polyline points="16 6 12 2 8 6"></polyline>
                        <line x1="12" y1="2" x2="12" y2="15"></line>
                      </svg>
                      {t('course-content.detail.sendForApproval')}
                    </Button>
                  )}
                  
                  {course.status === 'pending_approval' && (
                    <Button variant="primary" size="sm">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                      </svg>
                      {t('course-content.detail.approve')}
                    </Button>
                  )}
                  
                  {course.status === 'approved' && (
                    <Button variant="primary" size="sm">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                        <path d="M3 15v4c0 1.1.9 2 2 2h14a2 2 0 0 0 2-2v-4M17 9l-5 5-5-5M12 12.8V2.5"></path>
                      </svg>
                      {t('course-content.detail.publish')}
                    </Button>
                  )}
                  
                  <Button variant="outline" size="sm">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="15" y1="9" x2="9" y2="15"></line>
                      <line x1="9" y1="9" x2="15" y2="15"></line>
                    </svg>
                    {t('course-content.detail.cancel')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <ThemeSelectionDialog
        isOpen={isThemeDialogOpen}
        onClose={() => setIsThemeDialogOpen(false)}
        onSelectTheme={handleThemeSelect}
        currentTheme={selectedTheme || undefined}
      />

      {isEditDialogOpen && (
        <FullscreenCourseEditDialog 
          courseId={courseId} 
          onClose={() => setIsEditDialogOpen(false)}
        />
      )}
    </div>
  );
} 